import { gql } from '@apollo/client';

export const MAPPINGS_QUERY = gql`
    query GetDocumentMappings(
        $recordInfo: String!
        $clientMappingLookupDefinitionId: UUID!
        $includeClient: Boolean!
        $contactMappingLookupDefinitionId: UUID!
        $includeContact: Boolean!
        $articleMappingLookupDefinitionId: UUID!
        $includeArticle: Boolean!
        $vatIdCheckMappingLookupDefinitionId: UUID!
        $includeVatIdCheck: Boolean!
        $invoiceAddressMappingLookupDefinitionId: UUID!
        $includeInvoiceAddress: Boolean!
        $deliveryAddressMappingLookupDefinitionId: UUID!
        $includeDeliveryAddress: Boolean!
    ) {
        clientMappings(lookupDefinitionId: $clientMappingLookupDefinitionId, recordInfo: $recordInfo)
            @include(if: $includeClient) {
            id
            createdAt
            isIgnored
            highConfidence
            clientId
        }

        contactMappings(lookupDefinitionId: $contactMappingLookupDefinitionId, recordInfo: $recordInfo)
            @include(if: $includeContact) {
            id
            createdAt
            isIgnored
            highConfidence
            targetContactId
        }

        articleMappings(lookupDefinitionId: $articleMappingLookupDefinitionId, recordInfo: $recordInfo)
            @include(if: $includeArticle) {
            id
            createdAt
            isIgnored
            highConfidence
            targetArticleNumber
        }

        vatIdCheckMappings(lookupDefinitionId: $vatIdCheckMappingLookupDefinitionId, recordInfo: $recordInfo)
            @include(if: $includeVatIdCheck) {
            id
            createdAt
            isIgnored
            highConfidence
            vatId
        }

        invoiceAddressMappings: addressMappings(
            lookupDefinitionId: $invoiceAddressMappingLookupDefinitionId
            recordInfo: $recordInfo
        ) @include(if: $includeInvoiceAddress) {
            id
            createdAt
            isIgnored
            highConfidence
            targetAddressId
        }

        deliveryAddressMappings: addressMappings(
            lookupDefinitionId: $deliveryAddressMappingLookupDefinitionId
            recordInfo: $recordInfo
        ) @include(if: $includeDeliveryAddress) {
            id
            createdAt
            isIgnored
            highConfidence
            targetAddressId
        }
    }
`;

export const MAPPING_MUTATIONS = {
    updateClientMappingMutation: gql`
        mutation UpdateClientMapping(
            $id: UUID!
            $lookupDefinitionId: UUID!
            $isIgnored: Boolean
            $highConfidence: Boolean
        ) {
            updateClientMappingMutation(
                id: $id
                lookupDefinitionId: $lookupDefinitionId
                isIgnored: $isIgnored
                highConfidence: $highConfidence
            ) {
                mapping {
                    id
                    isIgnored
                    highConfidence
                }
            }
        }
    `,
    updateContactMappingMutation: gql`
        mutation UpdateContactMapping(
            $id: UUID!
            $lookupDefinitionId: UUID!
            $isIgnored: Boolean
            $highConfidence: Boolean
        ) {
            updateContactMappingMutation(
                id: $id
                lookupDefinitionId: $lookupDefinitionId
                isIgnored: $isIgnored
                highConfidence: $highConfidence
            ) {
                mapping {
                    id
                    isIgnored
                    highConfidence
                }
            }
        }
    `,
    updateArticleMappingMutation: gql`
        mutation UpdateArticleMapping(
            $id: UUID!
            $lookupDefinitionId: UUID!
            $isIgnored: Boolean
            $highConfidence: Boolean
        ) {
            updateArticleMappingMutation(
                id: $id
                lookupDefinitionId: $lookupDefinitionId
                isIgnored: $isIgnored
                highConfidence: $highConfidence
            ) {
                mapping {
                    id
                    isIgnored
                    highConfidence
                }
            }
        }
    `,
    updateVatIdCheckMappingMutation: gql`
        mutation UpdateVatIdCheckMapping(
            $id: UUID!
            $lookupDefinitionId: UUID!
            $isIgnored: Boolean
            $highConfidence: Boolean
        ) {
            updateVatIdCheckMappingMutation(
                id: $id
                lookupDefinitionId: $lookupDefinitionId
                isIgnored: $isIgnored
                highConfidence: $highConfidence
            ) {
                mapping {
                    id
                    isIgnored
                    highConfidence
                }
            }
        }
    `,
    updateAddressMappingMutation: gql`
        mutation UpdateAddressMapping(
            $id: UUID!
            $lookupDefinitionId: UUID!
            $isIgnored: Boolean
            $highConfidence: Boolean
        ) {
            updateAddressMappingMutation(
                id: $id
                lookupDefinitionId: $lookupDefinitionId
                isIgnored: $isIgnored
                highConfidence: $highConfidence
            ) {
                mapping {
                    id
                    isIgnored
                    highConfidence
                }
            }
        }
    `,
};

export const SEND_ASSISTANCE_EMAIL = gql`
    mutation ($documentType: DocumentTypeEnum!, $recordId: UUID!, $email: DocumentEmailInput!) {
        sendDocumentEmail(documentType: $documentType, recordId: $recordId, email: $email) {
            emailId
            errors
        }
    }
`;

export const GET_PROCESSING_RECORD_EMAILS = gql`
    query ($documentType: DocumentTypeEnum!, $recordId: UUID!) {
        documentEmails(documentType: $documentType, recordId: $recordId) {
            received {
                subject
                text
                html
                sender {
                    name
                    email
                }
                receivers {
                    name
                    email
                }
                attachments {
                    name
                    type
                    url
                }
            }
            sent {
                id
                createdAt
                subject
                body
                mimetype
                sender {
                    email
                    name
                }
                receivers {
                    email
                    name
                }
                carbonCopy {
                    email
                    name
                }
                blindCarbonCopy {
                    email
                    name
                }
                attachments {
                    name
                    url
                }
            }
            receiverSuggestions {
                name
                email
            }
            sourceFile {
                name
                url
            }
        }
    }
`;

export const GET_EMAIL_TEMPLATES = gql`
    query ($documentType: DocumentTypeEnum!, $recordId: UUID!) {
        documentEmails(documentType: $documentType, recordId: $recordId) {
            templates {
                id
                createdAt
                subject
                body
                mimetype
                sender {
                    email
                    name
                }
                receivers {
                    email
                    name
                }
                carbonCopy {
                    email
                    name
                }
                blindCarbonCopy {
                    email
                    name
                }
                attachments {
                    name
                    url
                }

                templateName
                isTemplate
            }
        }
    }
`;

export const CREATE_EMAIL_TEMPLATE = gql`
    mutation (
        $documentType: DocumentTypeEnum!
        $channelId: UUID!
        $templateName: String!
        $templateData: DocumentEmailInput!
    ) {
        createDocumentEmailTemplate(
            documentType: $documentType
            channelId: $channelId
            templateName: $templateName
            templateData: $templateData
        ) {
            templateId
            errors
        }
    }
`;

export const UPDATE_EMAIL_TEMPLATE = gql`
    mutation ($templateId: UUID!, $templateName: String!, $templateData: DocumentEmailInput!) {
        updateDocumentEmailTemplate(templateId: $templateId, templateName: $templateName, templateData: $templateData) {
            templateId
            errors
        }
    }
`;

export const DELETE_EMAIL_TEMPLATE = gql`
    mutation ($templateId: UUID!) {
        deleteDocumentEmailTemplate(templateId: $templateId) {
            errors
        }
    }
`;

export const ASSIGN_TO_DOCUMENTS = gql`
    mutation ($documentType: DocumentTypeEnum!, $recordIds: [UUID!]!, $emailAddress: String) {
        assignToDocuments(documentType: $documentType, recordIds: $recordIds, emailAddress: $emailAddress) {
            success
        }
    }
`;
